import { computed, reactive, readonly } from 'vue';
import { $fetch } from '@/utils/fetch';

export interface ProfileForm {
  name: string;
  idType: string;
  idNo: string;
  role: string;
  org: string;
  school: string;
  phone: string;
  email: string;
}

function emptyForm(): ProfileForm {
  return {
    name: '',
    idType: '身份证',
    idNo: '',
    role: '',
    org: '',
    school: '',
    phone: '',
    email: '',
  };
}

// 全局共享的个人信息状态：用户中心编辑、报名弹窗只读消费同一份
const state = reactive<ProfileForm>(emptyForm());
const readonlyState = readonly(state);

// 是否已尝试加载过档案（避免每次导航都打 check 接口）
const profileStatus = { loaded: false };

// 必填项是否已完善（姓名/证件类型/证件号码/手机号/邮箱）
const isComplete = computed(
  () =>
    state.name.trim() !== ''
    && state.idType.trim() !== ''
    && state.idNo.trim() !== ''
    && state.phone.trim() !== ''
    && state.email.trim() !== '',
);

function updateProfile(p: Partial<ProfileForm>) {
  Object.assign(state, p);
}

// 检查是否已填写个人信息；已填写则拉取并映射回 state
async function loadProfile() {
  const checkRes = await $fetch<{ has_profile: boolean }>('/api/web/profile/check', { errorMessage: false });
  if (!checkRes.isSuccess || !checkRes.data?.has_profile) {
    return { hasProfile: false };
  }

  const getRes = await $fetch<{
    real_name?: string;
    cert_type?: string;
    cert_no?: string;
    role?: string;
    org?: string;
    school?: string;
    phone?: string;
    email?: string;
  }>('/api/web/profile/get', { errorMessage: false });

  if (getRes.isSuccess && getRes.data) {
    updateProfile({
      name: getRes.data.real_name ?? '',
      idType: getRes.data.cert_type ?? '身份证',
      idNo: getRes.data.cert_no ?? '',
      role: getRes.data.role ?? '',
      org: getRes.data.org ?? '',
      school: getRes.data.school ?? '',
      phone: getRes.data.phone ?? '',
      email: getRes.data.email ?? '',
    });
  }

  return { hasProfile: true };
}

// 路由守卫调用：登录后首次导航加载一次档案，使首页等非用户中心页面也能拿到档案状态
async function ensureProfileLoaded() {
  if (profileStatus.loaded) {
    return;
  }

  profileStatus.loaded = true;
  await loadProfile();
}

// 保存个人信息（新建/更新）
async function saveProfile(form: ProfileForm) {
  const { isSuccess, msg } = await $fetch('/api/web/profile/update', {
    errorMessage: false,
    data: {
      real_name: form.name,
      cert_type: form.idType,
      cert_no: form.idNo,
      phone: form.phone,
      email: form.email,
      // 后端暂未支持，先一并提交，待后端补字段
      role: form.role,
      org: form.org,
      school: form.school,
    },
  });

  if (isSuccess) {
    updateProfile(form);
  }

  return { isSuccess, msg };
}

export function useProfile() {
  return {
    profile: readonlyState,
    isComplete,
    updateProfile,
    loadProfile,
    ensureProfileLoaded,
    saveProfile,
  };
}
